import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fromTable } from "@/integrations/supabase/db";
import { useToast } from "@/hooks/use-toast";

export interface BeratungSession {
  id: string;
  tenant_id: string | null;
  berater_id: string | null;
  user_id: string | null;
  /** Optional custom name for the consultation session */
  name: string | null;
  teilnehmer: Array<{
    id: string;
    typ: 'kunde' | 'kind' | 'partner' | 'lead';
    name: string;
    geburtsdatum?: string;
  }>;
  lead_vb_id: string | null;
  kundentyp: 'Privat' | 'Gewerbe';
  aktuelle_seite_id: string | null;
  ausgewaehlte_module: string[];
  status: 'aktiv' | 'pausiert' | 'abgeschlossen' | 'abgebrochen';
  cursor_position: { x: number; y: number; containerWidth: number } | null;
  drawing_content: { drawing: string; text: string; shared?: boolean; collaborative?: boolean } | null;
  /** Session-specific tile overrides keyed by seite_id */
  tile_overrides: Record<string, any[]>;
  /** Session-specific form values */
  form_values: Record<string, any>;
  gestartet_am: string;
  beendet_am: string | null;
  protokoll_url: string | null;
  created_at: string;
  updated_at: string;
}

interface UseBeratungSessionsOptions {
  kundeId?: string;
  beraterId?: string;
  status?: BeratungSession['status'][];
  enabled?: boolean;
}

interface UseBeratungSessionOptions {
  sessionId?: string;
  enabled?: boolean;
}

export function useBeratungSession({ sessionId, enabled = true }: UseBeratungSessionOptions) {
  return useQuery({
    queryKey: ['beratung-session', sessionId],
    queryFn: async (): Promise<BeratungSession | null> => {
      if (!sessionId) return null;

      const { data, error } = await fromTable('beratung_sessions')
        .select('*')
        .eq('id', sessionId)
        .single();

      if (error) throw error;

      const session = data as any;
      return {
        ...session,
        teilnehmer: (session.teilnehmer || []) as BeratungSession['teilnehmer'],
        ausgewaehlte_module: session.ausgewaehlte_module || [],
        cursor_position: session.cursor_position as BeratungSession['cursor_position'],
        tile_overrides: (session.tile_overrides || {}) as Record<string, any[]>,
        form_values: (session.form_values || {}) as Record<string, any>,
      } as BeratungSession;
    },
    enabled: enabled && !!sessionId,
  });
}

export function useBeratungSessions({ kundeId, beraterId, status, enabled = true }: UseBeratungSessionsOptions = {}) {
  return useQuery({
    queryKey: ['beratung-sessions', kundeId, beraterId, status],
    queryFn: async (): Promise<BeratungSession[]> => {
      // Query crm schema via fromTable helper
      let query = fromTable('beratung_sessions')
        .select('*')
        .order('gestartet_am', { ascending: false });

      if (beraterId) {
        query = query.eq('berater_id', beraterId);
      }

      if (status && status.length > 0) {
        query = query.in('status', status);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      let sessions = (data || []) as any[];
      
      // Filter by kunde_id in teilnehmer JSONB array (client-side since contains is tricky)
      if (kundeId) {
        sessions = sessions.filter(session => {
          const teilnehmer = session.teilnehmer as any[];
          return teilnehmer?.some(t => t.id === kundeId);
        });
      }
      
      return sessions.map(session => ({
        ...session,
        teilnehmer: (session.teilnehmer || []) as BeratungSession['teilnehmer'],
        ausgewaehlte_module: session.ausgewaehlte_module || [],
        cursor_position: session.cursor_position as BeratungSession['cursor_position'],
        tile_overrides: (session.tile_overrides || {}) as Record<string, any[]>,
        form_values: (session.form_values || {}) as Record<string, any>,
      })) as BeratungSession[];
    },
    enabled,
  });
}

interface CreateSessionData {
  teilnehmer: BeratungSession['teilnehmer'];
  kundentyp?: 'Privat' | 'Gewerbe';
  ausgewaehlte_module?: string[];
  berater_id?: string;
  lead_vb_id?: string;
}

export function useCreateBeratungSession() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateSessionData) => {
      const { data: result, error } = await fromTable('beratung_sessions')
        .insert({
          teilnehmer: data.teilnehmer,
          kundentyp: data.kundentyp || 'Privat',
          ausgewaehlte_module: data.ausgewaehlte_module || [],
          berater_id: data.berater_id,
          lead_vb_id: data.lead_vb_id,
          status: 'aktiv',
        })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beratung-sessions'] });
      toast({
        title: "Beratung gestartet",
        description: "Die Beratungssession wurde erfolgreich erstellt.",
      });
    },
    onError: (error) => {
      console.error('Error creating session:', error);
      toast({
        title: "Fehler",
        description: "Die Beratung konnte nicht gestartet werden.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateBeratungSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<BeratungSession> & { id: string }) => {
      const { data: result, error } = await fromTable('beratung_sessions')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: (_data, variables) => {
      // Invalidate both list and individual session queries
      queryClient.invalidateQueries({ queryKey: ['beratung-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['beratung-session', variables.id] });
    },
  });
}
